import { Component } from '@theme/component'
import { ThemeEvents } from '@theme/events'
import { onAnimationEnd, parseItemCountFromSectionsHTML } from '@theme/utils'

const CartIconConfig = {
  cacheTtlMs: 10_000,
  maxDisplayCount: 99
}

class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount']

  get currentCartCount() {
    return Number.parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10)
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent =
      value <= CartIconConfig.maxDisplayCount ? String(value) : ''
  }

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate)
    this.ensureCartBubbleIsCorrect()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate)
  }

  onCartUpdate = async (event) => {
    if (event.detail.data?.didError) {
      return
    }
    const itemCount = this.#getItemCountFromEvent(event)
    if (typeof itemCount !== 'number') {
      return
    }
    const shouldAnimate = event.detail.data?.source === 'ui-product-form'
    this.renderCartBubble(itemCount, shouldAnimate)
  }

  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0)
    this.refs.cartBubble.classList.toggle('hidden', itemCount === 0)
    this.refs.cartBubble.classList.toggle(
      'cart-bubble--animating',
      itemCount > 0 && animate
    )
    this.currentCartCount = itemCount
    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0)
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        timestamp: Date.now(),
        value: String(this.currentCartCount)
      })
    )
    if (!animate) {
      return
    }
    await onAnimationEnd(this.refs.cartBubbleText)
    this.refs.cartBubble.classList.remove('cart-bubble--animating')
  }

  ensureCartBubbleIsCorrect = () => {
    const cached = sessionStorage.getItem('cart-count')
    if (cached === null) {
      return
    }
    try {
      const { value, timestamp } = JSON.parse(cached)
      if (Date.now() - timestamp >= CartIconConfig.cacheTtlMs) {
        return
      }
      const count = Number.parseInt(value, 10)
      if (Number.isNaN(count) || count < 0) {
        return
      }
      if (String(count) === this.refs.cartBubbleCount.textContent) {
        return
      }
      this.renderCartBubble(count, false)
      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
    } catch {}
  }

  #getItemCountFromEvent(event) {
    const detail = event?.detail
    if (!detail) {
      return null
    }

    const resourceCount = detail.resource?.item_count
    if (typeof resourceCount === 'number') {
      return resourceCount
    }

    const dataCount = detail.data?.itemCount
    if (typeof dataCount === 'number') {
      return dataCount
    }

    return parseItemCountFromSectionsHTML(detail.data?.sections)
  }
}

if (!customElements.get('ui-cart-icon')) {
  customElements.define('ui-cart-icon', CartIcon)
}
